import React, { useCallback, useState } from 'react';

import { Box, Header, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { GestureResponderEvent } from 'react-native';

import { LayoutContainer } from 'components/layout/styled';
import { useRoute } from 'hooks/navigation/use-route';
import { AuthLinkAction } from 'pages/authentication/components/auth-actions';
import { AuthContainer } from 'pages/authentication/components/auth-container';
import UnauthRequired from 'pages/authentication/components/unauth-required';
import { useAuthContext } from 'state/auth';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { useErrorContext } from 'state/errors';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

const TransformedHeadline = Header.withConfig({
  fontSize: '4xl',
  lineHeight: 'lg',
});

const CenteredText = Text.withConfig({
  justifyContent: 'center',
});

const NotReceived = Box.withConfig({
  borderTopWidth: 1,
  borderTopColor: Styles.color.disabledBorder,
  marginTop: '$8',
  paddingTop: '$4',
  width: 'full',
  justifyContent: 'center',
});

const ResendForm = Box.withConfig({
  width: 'full',
  justifyContent: 'center',
});

const Separator = Box.withConfig({
  width: 'full',
  paddingY: '$1',
});

interface IAuthChallengeProps {
  email?: string;
}

const AuthJwtChallengeScreen = ({ email }: IAuthChallengeProps) => {
  const { email: navigationParamsEmail } = useAuthFlow();
  const userEmail = email || navigationParamsEmail || LocalStorage.getItem(StorageKeys.LOGIN) || '';
  const { signIn } = useAuthContext();
  const { handleError } = useErrorContext();
  const [loading, setLoading] = useState(false);
  const { formatMessage } = useIntl();
  const { pathname } = useRoute();

  const sendAgain = useCallback(
    async (event: GestureResponderEvent) => {
      try {
        setLoading(true);
        event.preventDefault();

        await signIn({
          email: userEmail,
          navigateOnSuccess: false,
        });
      } catch (error) {
        // todo: error state
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        handleError(error, { location: pathname });
      } finally {
        setLoading(false);
      }
    },
    [signIn, userEmail, handleError, pathname]
  );

  const notReceivedChildren = formatMessage(
    { id: 'haveNotReceivedAuthEmail' },
    {
      link: (
        <ResendForm>
          <AuthLinkAction
            primaryActionQaId="send-again-button"
            primaryActionLabel={formatMessage({ id: 'requestNewLinkAuthEmail' })}
            primaryActionIsLoading={loading}
            // @ts-expect-error TS(2322) FIXME: Type '(event: GestureResponderEvent) => Promise<vo... Remove this comment to see the full error message
            onPress={sendAgain}
          />
        </ResendForm>
      ),
    }
  );

  return (
    <UnauthRequired>
      <LayoutContainer>
        <AuthContainer>
          <TransformedHeadline>
            {formatMessage({ id: 'authEmailCheckYourEmail' })}
          </TransformedHeadline>
          <CenteredText>
            {formatMessage({ id: 'authEmailSentEmail' })}
            <Separator />
            <Text fontWeight="bold" data-private testID="auth-challenge-email">
              {userEmail}
            </Text>
          </CenteredText>
          <CenteredText paddingTop="$4">
            {formatMessage({ id: 'authEmailTimeToReceive' }, { separator: <Text> </Text> })}
          </CenteredText>
          <NotReceived>{notReceivedChildren}</NotReceived>
        </AuthContainer>
      </LayoutContainer>
    </UnauthRequired>
  );
};

export default AuthJwtChallengeScreen;
